import { memo } from 'react';

import { Pill, Tooltip } from '@/ui';
import type { Props } from '@/ui/components/AppointmentAttendanceStatus/types';
import snakeCaseToTitleCase from '@/utils/snakeToTitleCase';

type PillColor = 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';

/**
 * NOTE: This component is business/domain-specific and does not belong in the
 * generic UI primitives library (ui/components). It should be moved to a more
 * appropriate domain-specific location in the codebase.
 */
const AppointmentAttendanceStatus = ({ status, reason }: Props) => {
  let emoji = '';
  let color: PillColor;
  let formattedStatus = snakeCaseToTitleCase(status);
  let explanation = '';
  switch (status) {
    case 'attended':
      explanation = 'The patient attended the appointment.';
      emoji = '🎉';
      color = 'primary';
      break;
    case 'no_show':
      explanation = 'The patient did not show up for the appointment.';
      emoji = '';
      color = 'destructive';
      break;
    case 'pending':
      explanation = "The appointment hasn't happened yet.";
      emoji = '🕒';
      color = 'neutral';
      break;
    case 'pending_attendance':
      explanation =
        "The appointment is ongoing or recently concluded but attendance hasn't been recorded yet.";
      emoji = '🕒';
      color = 'neutral';
      break;
    case 'confirmed':
      explanation = 'The appointment is confirmed by the member.';
      emoji = '';
      color = 'primary';
      break;
    case 'cancelled':
      explanation = 'The appointment was cancelled.';
      emoji = '❌';
      color = 'destructive';
      break;
    case 'auto_cancelled':
      explanation = 'The appointment was automatically cancelled.';
      emoji = '🚫';
      color = 'destructive';
      break;
    case 'declined':
      explanation = 'The appointment was declined by the member.';
      emoji = '🙅';
      color = 'destructive';
      formattedStatus = 'Declined by Member';
      break;
    case 'rescheduled':
      explanation = 'The appointment was rescheduled.';
      emoji = '🔄';
      color = 'secondary';
      break;
    case 'not_recorded':
      explanation = 'The appointment has no recorded status';
      emoji = '';
      color = 'neutral';
      break;
    default:
      explanation = 'The patient has an unknown attendance status.';
      emoji = '';
      if (!status) {
        formattedStatus = 'Unknown';
      }
      color = 'neutral';
      break;
  }

  if (reason) {
    explanation = `Reason: ${reason.name}${reason.comment ? ` - ${reason.comment}` : ''}`;
  }

  return (
    <Tooltip content={explanation}>
      <Pill color={color} size="sm">
        {emoji ? `${emoji} ` : ''}
        {formattedStatus}
      </Pill>
    </Tooltip>
  );
};

export default memo(AppointmentAttendanceStatus);
